import {
  AdditionalConfiguration,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
export class SustainabilityFitMetricConfiguration extends ReportSectionConfigurationBase {
  typeName = 'SustainabilityFitMetricConfiguration';
  nodataLocalizationKeyName = 'sustainabilityFitMetric';
  readonly reportSection = ReportSection.SustainabilityFitMetric;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_SustainabilityFitMetric];
  override additionalConfiguration: AdditionalConfiguration = {
    isGraphSelected: true
  };
  get selectedCriteriaCount(): number {
    const gridSection = this.availableDataSections.find(section => section.name === 'sustainabilityFitMetricGrid');
    if (!gridSection || !gridSection.subSections) {
      return 0;
    }
    return gridSection.subSections.filter(
      subSection =>
        [
          'sustainabilityFitMetricGridTopCriteria',
          'sustainabilityFitMetricGridBottomCriteria',
          'sustainabilityFitMetricGridPreferences'
        ].includes(subSection.name) && subSection.isSelected
    ).length;
  }

  get minSize(): number {
    const gridSection = this.availableDataSections.find(section => section.name === 'sustainabilityFitMetricGrid');
    if (gridSection && gridSection.isSelected && this.additionalConfiguration.isGraphSelected) {
      return 2;
    }
    return 1;
  }

  get maxSize(): number {
    const gridSection = this.availableDataSections.find(section => section.name === 'sustainabilityFitMetricGrid');
    if (gridSection && gridSection.isSelected) {
      if (this.additionalConfiguration.isGraphSelected) {
        if (this.selectedCriteriaCount > 1) {
          return 4;
        } else {
          return 2;
        }
      }
    }
    return 4;
  }

  get defaultSize(): number {
    return this.maxSize;
  }

  override readonly configurationControls = [
    {
      name: 'sustainabilityFitMetricGridGraph',
      displayName: 'Sustainability Criteria Graph (for all selected criteria)',
      type: 'graphControl'
    }
  ];

  readonly availableDataSections = [
    {
      name: 'sustainabilityFitMetricFit',
      displayName: 'Sustainability Fit',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'sustainabilityFitMetricGrid',
      displayName: 'Sustainability Crieria',
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'sustainabilityFitMetricGridTopCriteria',
          displayName: 'Top criteria',
          isSelected: true,
          canConfigure: true
        },
        {
          name: 'sustainabilityFitMetricGridBottomCriteria',
          displayName: 'Bottom criteria',
          isSelected: true,
          canConfigure: true
        },
        {
          name: 'sustainabilityFitMetricGridPreferences',
          displayName: 'Preferences',
          isSelected: true,
          canConfigure: true
        }
      ],
      canReorderSubSections: false
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactSustainabilityFitMetric';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionSustainabilityFitMetric';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Sustainability Fit Metric';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface SustainabilityFitMetricData {
  [ReportViewModelSection.InvestorProfile_SustainabilityFitMetric]: {
    sustainabilityFitMetric: SustainabilityFitMetric;
    culture: string;
  };
}
export interface SustainabilityFitMetric {
  factors: FactorScore[];
  sustainabilityFit: number;
}

export interface FactorScore {
  criteriaId: number;
  criteriaName: string;
  score: number;
  securityDecile: number;
  investorDeciles: InvestorDeciles[];
}

export interface InvestorDeciles {
  decile: number;
  concentration: number;
}

export interface Decile {
  values: number[];
}
